/**
 * Builds API query parameters following JSON:API conventions
 *
 * @param {Object} params - Client-side parameters
 * @param {number} [params.page] - Page number
 * @param {number} [params.limit] - Items per page
 * @param {Object} [params.filter] - Filter object with field-value pairs
 * @param {string} [params.sort] - Sort field (prefix with '-' for descending)
 * @param {Array<string>} [params.include] - Related resources to include
 * Any additional top-level key in params is passed through as query string
 * @returns {Object} API-formatted query parameters
 *
 * @example
 * buildApiParams({
 *   page: 2,
 *   limit: 20,
 *   filter: { email: 'test@example.com', role: 'admin' },
 *   sort: '-createdAt',
 *   include: ['company', 'sessions'],
 *   parentId: 42
 * })
 * // Returns:
 * // {
 * //   'page[number]': 2,
 * //   'page[size]': 20,
 * //   'filter[email]': 'test@example.com',
 * //   'filter[role]': 'admin',
 * //   sort: '-createdAt',
 * //   include: 'company,sessions',
 * //   parentId: 42
 * // }
 */
export const buildApiParams = (params = {}) => {
  const {
    page,
    limit,
    filter,
    include,
    ...extraParams
  } = params

  const apiParams = {}

  // Pagination
  if (page !== undefined) {
    apiParams['page[number]'] = page
  }
  if (limit !== undefined) {
    apiParams['page[size]'] = limit
  }

  if (filter && typeof filter === 'object') {
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        apiParams[`filter[${key}]`] = value
      }
    })
  }
  if (include && Array.isArray(include) && include.length > 0) {
    apiParams.include = include.join(',')
  }
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      apiParams[key] = value
    }
  })

  return apiParams
}
